import { RUN_LINK_ARRIVAL } from "@libs/OperatorChat/navigationState";
import {
  OPERATOR_CHAT_PATH,
  OPERATOR_ROUTINES_PATH,
  operatorChatTaskPath,
  operatorRoutinePath,
} from "@libs/OperatorChat/paths";
import { useSession } from "@store/session";
import { useLocation, useParams, useSearchParams } from "react-router";
import { CompanyNavigate } from "./CompanyNavigate";

// The retired Operator pages' links, which notifications, reports and
// bookmarks still carry, and the routines' old address. Each opens its place
// in the Operator chat route and replaces itself in the history, so Back
// returns to where it was opened.

/** The page restricted roles read the Operator on: they cannot open the chat. */
const OPERATOR_REVIEW_PATH = "/operator/review";

/**
 * The old Operator home, and the old health report links that opened it: the
 * Operator chat, or the Review page for whoever cannot open the chat.
 */
export const OperatorHomeRedirect = () => {
  const { userIsAuthorized } = useSession();
  return (
    <CompanyNavigate
      to={
        userIsAuthorized(OPERATOR_CHAT_PATH)
          ? OPERATOR_CHAT_PATH
          : OPERATOR_REVIEW_PATH
      }
      replace
    />
  );
};

/** The old Recipes and Automations pages: routines, or Review for readers. */
export const OperatorRoutinesRedirect = () => {
  const { userIsAuthorized } = useSession();
  if (!userIsAuthorized(OPERATOR_CHAT_PATH)) return <OperatorHomeRedirect />;
  return <CompanyNavigate to={OPERATOR_ROUTINES_PATH} replace />;
};

/**
 * An old link to one run: the routines, told the link was to a single run,
 * since each routine's runs now live in its own chat.
 */
export const OperatorRunLinkRedirect = () => {
  const { userIsAuthorized } = useSession();
  if (!userIsAuthorized(OPERATOR_CHAT_PATH)) return <OperatorHomeRedirect />;
  return (
    <CompanyNavigate
      to={OPERATOR_ROUTINES_PATH}
      replace
      state={RUN_LINK_ARRIVAL}
    />
  );
};

/**
 * An old automation's history: the routine in the routines view, or, when
 * the link pinned one of its runs (`?taskId=`), that run opened as its
 * conversation.
 */
export const OperatorAutomationRedirect = () => {
  const { userIsAuthorized } = useSession();
  const { automationId } = useParams();
  const [search] = useSearchParams();
  if (!userIsAuthorized(OPERATOR_CHAT_PATH)) return <OperatorHomeRedirect />;
  const pinnedTaskId = search.get("taskId");
  const to = pinnedTaskId
    ? operatorChatTaskPath(pinnedTaskId)
    : automationId
      ? operatorRoutinePath(automationId)
      : OPERATOR_ROUTINES_PATH;
  return <CompanyNavigate to={to} replace />;
};

/**
 * The routines' address before they were called routines
 * (`/operator/chat/scheduled[/:automationId]`): the same view, and the same
 * routine, at the new address. The rest of the link (its query,
 * a fragment) means the same there, so it is kept.
 */
export const OperatorScheduledViewRedirect = () => {
  const { automationId } = useParams();
  const { search, hash } = useLocation();
  const path = automationId
    ? operatorRoutinePath(automationId)
    : OPERATOR_ROUTINES_PATH;
  return <CompanyNavigate to={`${path}${search}${hash}`} replace />;
};

/** An old Task result: the Task's run, opened as its conversation. */
export const OperatorTaskRedirect = () => {
  const { userIsAuthorized } = useSession();
  const { taskId } = useParams();
  if (!userIsAuthorized(OPERATOR_CHAT_PATH)) return <OperatorHomeRedirect />;
  return (
    <CompanyNavigate
      to={taskId ? operatorChatTaskPath(taskId) : OPERATOR_ROUTINES_PATH}
      replace
    />
  );
};
